import { ROUTES } from 'src/constants/route';

/**
 * Converts routes to the appropriate route for the current environment.
 *
 * This function automatically detects environment prefixes in the current URL and applies
 * the same prefix to any input route. It normalizes input routes by removing any existing
 * prefixes to prevent double prefixes, then applies the current environment's prefix.
 *
 * @param route - Route path to convert (e.g., '/learn/s', '/su/learn/s', '/learn/topic/123')
 * @returns Environment-appropriate route with the same prefix as current environment
 *
 * @example
 * // Current environment: /su-test/learn
 * getAppRoute('/learn/s') → '/su-test/learn/s'
 * getAppRoute('/su/learn/s') → '/su-test/learn/s' (removes /su prefix, applies /su-test)
 * getAppRoute('/staging/learn/topic/123') → '/su-test/learn/topic/123' (removes /staging, applies /su-test)
 *
 * // Current environment: /learn (production, no prefix)
 * getAppRoute('/learn/s') → '/learn/s' (unchanged)
 * getAppRoute('/su/learn/s') → '/learn/s' (removes /su prefix)
 */
export const getAppRoute = (route: string): string => {
  // Input validation and normalization
  if (!route || typeof route !== 'string') {
    return '';
  }

  // Normalize input route: extract everything from /learn onwards to remove any existing prefix
  const learnIndex = route.indexOf(ROUTES.LEARN.BASE);
  const cleanRoute = learnIndex >= 0 ? route.substring(learnIndex) : route;

  // Only apply prefix logic to routes that contain /learn
  if (learnIndex < 0) {
    return route; // Return original route unchanged if it doesn't contain /learn
  }

  // Detect current environment prefix
  const currentPath = window.location.pathname;
  const currentLearnIndex = currentPath.indexOf(ROUTES.LEARN.BASE);

  if (currentLearnIndex > 0) {
    // Extract environment prefix and apply to clean route
    const envPrefix = currentPath.substring(0, currentLearnIndex);
    return `${envPrefix}${cleanRoute}`;
  }

  // Production environment (no prefix)
  return cleanRoute;
};

/**
 * Merges URL parameters from a parameter string into a base URL.
 *
 * This function merges parameters by:
 * 1. Preserving existing parameters in the base URL (existing parameters take precedence)
 * 2. Adding new parameters that don't conflict with existing ones
 * 3. Using proper URL formatting (? vs &) based on whether parameters already exist
 *
 * @param baseUrl - The base URL that may or may not have existing parameters
 * @param newParamsString - Parameter string to merge (e.g., "?ref=email&.." or "ref=email&..")
 * @returns URL with merged parameters, existing parameters take precedence over new ones
 *
 * @example
 * mergeUrlParameters('https://webinar.com/join/123', '?ref=email&webinarId=456')
 * → 'https://webinar.com/join/123?ref=email&webinarId=456'
 *
 * mergeUrlParameters('https://webinar.com/join/123?token=xyz&webinarId=789', '?ref=email&webinarId=456')
 * → 'https://webinar.com/join/123?token=xyz&webinarId=789&ref=email' (existing webinarId=789 wins)
 */
export const mergeUrlParameters = (baseUrl: string, newParamsString: string): string => {
  // Input validation
  if (!baseUrl || typeof baseUrl !== 'string') {
    return '';
  }

  // If no new parameters to merge, return original URL
  if (!newParamsString || !newParamsString.replace(/^\?/, '').trim()) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl);

    // Parse new parameters, removing leading ? if present
    const cleanParamsString = newParamsString.startsWith('?') ? newParamsString.slice(1) : newParamsString;
    const newParams = new URLSearchParams(cleanParamsString);

    // Add new parameters only if they don't already exist (existing parameters win)
    newParams.forEach((value, key) => {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  } catch (error) {
    return baseUrl;
  }
};
